import * as React from 'react';
import * as TreeRepository from '../../../Repository/TreeRepository';
import message from 'antd/es/message';
import DBService, { TABLE_TREES } from '../../../Service/DBService';
import SyncQueueList from "./SyncQueueList";
import SyncQueueProgressBar from "./SyncQueueProgressBar";
import PQueue from 'p-queue';
import NetworkConnectivityException from "../../../Exception/NetworkConnectivityException";
import { v4 as uuidv4 } from 'uuid';
import { Alert, Button, Modal, Spin } from 'antd';
import { describeError, isConnectivityError } from '../../../Helper/ErrorHelper';
import { describeFormData, entriesToFormData, integerOrNull } from '../../../Helper/DataHelper';
import { watchPosition } from "../../../Service/GeolocationService";
import { TreeFormData } from '../../../Repository/TreeRepository';
import { SyncQueueItemData } from './SyncQueueItem';
import { ConnectionState, GlobalContext } from "../../../Contexts/GlobalContext";
import { invalidateTreesListVersion } from '../../../Service/MapTreesListService';
import { NO_NETWORK_CONNECTIVITY, treeNotSynced } from '../../../Helper/BannerMessages';

interface SyncQueueProps {
  treetypesList: any;
  updateTrees?: Function;
  /** Set while a tree is being compressed and queued: the count then gives way to a spinner. */
  saving?: boolean;
}

interface SyncQueueState {
  syncing: boolean;
  isFullSync: boolean;
  offlineQueueLength: number;
  openModal: boolean;
  /** Bumped on every opening: it remounts the queue list (see the `key` below). */
  queueListKey: number;
  currentPage: number,
  maxPages?: number,
  treesKeys: number[];
  treesData: SyncQueueItemData[];
  isCountingTrees: boolean;
  loading: boolean;
  hasFetchError: boolean,
  syncErrorMessage?: string;
  syncErrorIsConnectivity: boolean;
  syncSuccess: boolean;
  syncedCount: number;
  totalTreesInQueue: number;
  currentTreesSynced: number;
  initialTotalTreesInQueue: number;
  /** What the browser says this site already stores, and the space it allows it in total. */
  storageUsage?: number;
  storageQuota?: number;
}

export default class SyncQueue extends React.Component<SyncQueueProps, SyncQueueState> {
  private promiseQueue: PQueue;
  public nbTreesPerPage: number = 10;
  private treesSyncedInBatch: number = 0;
  // Kept out of the state on purpose: setState is asynchronous, so reading these
  // back from the state inside the sync loop could still see the values of the
  // previous run and abort a retry before it even started.
  private syncAborted: boolean = false;
  private syncFailure: { message: string, isConnectivity: boolean } | null = null;
  static contextType = GlobalContext;
  declare context: React.ContextType<typeof GlobalContext>

  constructor(props: SyncQueueProps) {
    super(props);
    this.promiseQueue = new PQueue({ concurrency: 3 });

    this.sync = this.sync.bind(this);
    this.deleteItem = this.deleteItem.bind(this);
    this.createItem = this.createItem.bind(this);
    this.countRecordsToSync = this.countRecordsToSync.bind(this);
    this.fetchQueueData = this.fetchQueueData.bind(this);
    this.state = {
      treesData: [],
      treesKeys: [],
      currentPage: 1,
      maxPages: undefined,
      syncing: false,
      isCountingTrees: true,
      isFullSync: false,
      openModal: false,
      queueListKey: 0,
      loading: true,
      hasFetchError: false,
      syncErrorMessage: undefined,
      syncErrorIsConnectivity: false,
      offlineQueueLength: 0,
      syncSuccess: false,
      syncedCount: 0,
      totalTreesInQueue: 0,
      currentTreesSynced: 0,
      initialTotalTreesInQueue: 0,
      storageUsage: undefined,
      storageQuota: undefined,
    };
  }
  /*
    async updateOfflineQueueLength() {
      const indexes = await TreeRepository.getIndexes();
      this.setState({
        offlineQueueLength: JSON.parse(JSON.stringify(indexes.length ? indexes.length : 0))
      });
    }
  */
  async componentDidMount() {
    await DBService.openDb();
    this.countRecordsToSync();
    this.refreshStorageEstimate();
  }

  // A GPS position lands about once a second and re-renders the whole form with it, which is what keeps the
  // accuracy on screen live - and nothing of that belongs to the queue: it draws from its own state and from
  // the props compared here, so a render of the page leaves it - and the list, with every thumbnail in it -
  // alone while positions keep arriving.
  shouldComponentUpdate(nextProps: SyncQueueProps, nextState: SyncQueueState) {
    return nextState !== this.state
      || nextProps.saving !== this.props.saving
      || nextProps.treetypesList !== this.props.treetypesList
      || nextProps.updateTrees !== this.props.updateTrees;
  }

  // The trees and their photos are kept in the browser's storage until they are synced, and nothing more
  // can be written once the space the browser allows this site is used up: the write fails with a
  // QuotaExceededError and the tree is lost. The browser reports what is stored and the `quota` it grants
  // the site - a figure the browser chooses rather than the room left on the device, Chromium currently
  // reporting a flat 2 GB for every site - so it is read as a budget to stay well under. Read when the
  // queue mounts and after each tree saved in it, which are the moments that figure changes.
  async refreshStorageEstimate() {
    if (!navigator.storage?.estimate) return;

    try {
      const { usage, quota } = await navigator.storage.estimate();
      this.setState({ storageUsage: usage || 0, storageQuota: quota || 0 });
    } catch (error) {
      console.debug('TreeWallet: could not read the storage estimate', error);
    }
  }

  // Warned about well before the limit is reached: a tree that cannot be written is a tree lost, and the
  // browser gives no warning of its own before the write fails.
  renderStorageWarning() {
    const { storageUsage, storageQuota } = this.state;
    if (!storageUsage || !storageQuota) return null;

    const usedRatio = storageUsage / storageQuota;
    if (usedRatio < 0.8) return null;

    const inGb = (bytes: number) => `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    const almostFull = usedRatio >= 0.95;

    return (
      <Alert
        type={almostFull ? 'error' : 'warning'}
        showIcon
        style={{ marginBottom: '10px' }}
        message={almostFull ? 'Storage almost full on this device' : 'Storage filling up on this device'}
        description={`${inGb(storageUsage)} of the ${inGb(storageQuota)} Max quota of trees in the queue reached. Please sync the queue to free space and collect more trees`}
      />
    );
  }

  hasDataInQueue() {
    const { treesData } = this.state;
    return treesData.length > 0;
  }

  async countRecordsToSync() {
    const indexes = await TreeRepository.getIndexes();
    const totalTreesInQueue = await TreeRepository.getTotalTreeCount();
    const newState = {
      offlineQueueLength: JSON.parse(JSON.stringify(indexes.length ? indexes.length : 0)),
      treesKeys: JSON.parse(JSON.stringify(indexes)).reverse(),
      maxPages: Math.floor(indexes.length / this.nbTreesPerPage),
      isCountingTrees: false,
      totalTreesInQueue
    };

    // Use setState with a callback to ensure the state is updated
    await new Promise<void>((resolve) => {
      this.setState({ ...newState }, () => resolve());
    });
    return newState;
  }

  async sync(id?: number) {
    const { updateTrees, contract } = this.context;
    this.syncAborted = false;
    this.syncFailure = null;
    this.setState({ hasFetchError: false, syncErrorMessage: undefined, syncErrorIsConnectivity: false })
    try {
      if (!navigator.onLine) {
        message.warning(NO_NETWORK_CONNECTIVITY)
        throw new NetworkConnectivityException('Network not available');
      }

      let incrementOfNewTrees = 0;
      if (undefined !== id) {
        await this.syncOne(id);
        return;
      } else {
        await this.syncAll();
        incrementOfNewTrees = this.treesSyncedInBatch;
      }
      // Same call as the single item below: the contract's count, asked of the server. The sync has just
      // been awaited, so the trees are there to be counted.
      updateTrees && updateTrees(incrementOfNewTrees, contract && contract.id);

    } catch (err) {
      this.syncAborted = true;
      // Nothing was synced: this one asks the server, so the count resyncs after a failure.
      updateTrees && updateTrees(null, contract && contract.id);

      // The reason recorded by the item that actually failed is more precise
      // than the exception bubbling up here, which can be the batch abort.
      const failure = this.syncFailure || {
        message: describeError(err),
        isConnectivity: isConnectivityError(err),
      };

      this.setState(
        {
          hasFetchError: true,
          isFullSync: false,
          syncing: false,
          loading: true,
          syncErrorMessage: failure.message,
          syncErrorIsConnectivity: failure.isConnectivity,
        },
        async () => {
          await this.fetchQueueData();
        }
      );
    }
  }

  async syncOne(id: number) {
    const { updateTrees, contract } = this.context;
    await this.setState({ isFullSync: false, syncing: true });
    const item = await TreeRepository.getOne(id, (err: any) => message.error(`${err} ${id}`));
    const treeData = this.formatTreeData(item, id);
    await this.syncItem(treeData, true)
    await this.setState({ isFullSync: false, syncing: false });
    // The tree that was just sent is gone from the storage this site is allowed, so the figure read when
    // it was collected is no longer the one to warn about. Not reached when the sync fails: a tree the
    // server refused is still stored, and still taking its space.
    this.refreshStorageEstimate();
    // Same as the whole-queue sync, with the contract so the figure is that contract's count.
    updateTrees && updateTrees(treeData.data.number, contract && contract.id);
  }

  async syncAll() {
    const { treesKeys, totalTreesInQueue } = this.state;

    // Store the initial total to keep it fixed during progress display
    this.setState({ initialTotalTreesInQueue: totalTreesInQueue });

    // sync all trees
    this.setState({ isFullSync: true, syncing: true });
    this.treesSyncedInBatch = 0;
    let treesProcessedCount = 0;

    try {
      // treesKeys is newest-first for the list; the API numbers the trees as it inserts them, so the
      // batch has to go out oldest-first or the ids run backwards through the day.
      for (const currentId of [...treesKeys].reverse()) {
        if (this.syncAborted) {
          throw new Error('sync aborted');
        }

        const itemData = await TreeRepository.getOne(currentId, (err: any) => message.error(`${err} ${currentId}`));
        const item = this.formatTreeData(itemData, currentId);
        await this.syncItem(item, false);

        const treesInThisHole = integerOrNull(item.data.number) || 0;
        this.treesSyncedInBatch += treesInThisHole;
        // The progress bar is incremented by the number of trees inside each tree hole
        // Only tree holes that really made it are added, since a failure throws before this line
        treesProcessedCount += treesInThisHole;
        this.setState({ currentTreesSynced: treesProcessedCount });
      }
    } finally {
      // GPS has to be restarted even when the batch fails, otherwise the form
      // stays stuck on "Waiting for GPS" until the app is reloaded.
      watchPosition();

      // Check storage free memory
      this.refreshStorageEstimate();
    }

    this.setState({ isFullSync: false, syncing: false, syncSuccess: true, syncedCount: this.treesSyncedInBatch });
  }

  async syncItem(treeData: SyncQueueItemData, updateQueue: boolean) {
    if (!['pending', 'error', 'canceled'].includes(treeData.status)) {
      return;
    }
    if (updateQueue) {
      this.setState({
        treesData: this.updateTreeData([{ ...treeData, status: 'in-progress' }]),
        syncing: true
      });
    }

    try {
      await this.createItem(treeData.data, treeData.idbId);
    } catch (err) {
      const isConnectivity = isConnectivityError(err);
      this.syncFailure = { message: describeError(err), isConnectivity };

      if (isConnectivity) {
        message.info(NO_NETWORK_CONNECTIVITY);
        console.error('no network connectivity', { error: err });
      }
      else {
        console.error('Could not sync tree', err, treeData);
        message.error(treeNotSynced(treeData.idbId, describeError(err)));
      }

      throw err;
    }
  }

  async createItem(data: any, idbId: number) {
    let photo = data['photo'];

    // Any string here is a URL the page can read: a `data:` one, a `blob:` one from the browser store, or the
    // shell's `/__queue-photo/<name>` route for a photo the app keeps as a file
    if (typeof photo === 'string' && photo) {
      try {
        const response = await fetch(photo);
        const contentType = response.headers.get('content-type') || '';

        // Anything but a picture is not the photo: the API would take it as an upload and answer a 500
        if (response.ok && (!contentType || contentType.startsWith('image/'))) {
          photo = await response.blob();
        } else {
          console.error('the photo url did not answer a picture', { url: photo, status: response.status, contentType });
          photo = null;
        }
      } catch (e) {
        console.error("Failed to fetch the photo from its URL:", photo, e);
        photo = null;
      }
    }

    // In Safari/WebKit environments, objects restored from IndexedDB may lose standard prototype
    if (!(photo instanceof Blob) && photo && typeof photo === 'object' && photo.size !== undefined) {
      try {
        photo = new Blob([photo], { type: photo.type || 'image/jpeg' });
      } catch (e) {
        console.error("Failed to construct Blob from photo object:", e);
      }
    }

    // Posting anything else as `photo` gives the API a text field where it
    // expects an upload, which it answers with a 500 that says nothing.
    if (!(photo instanceof Blob)) {
      console.error('tree has no usable photo', { idbId, photo });
      throw new Error('the photo of this tree could not be read, it cannot be synced');
    }

    const fullData = {
      ...data,
      // A tree whose fix had no altitude keeps none: `entriesToFormData` leaves the field out instead of
      // sending the made-up 0 the API used to receive and store
      photo: photo
    };

    if (this.checkOnlineStatus()) {
      throw new NetworkConnectivityException('no network connectivity');
    }

    const formData = entriesToFormData(Object.entries(fullData));
    if (!formData.has('uuid')) {
      formData.set('uuid', uuidv4());
    }

    // createTree rejects with the real exception: a connectivity failure stays a
    // connectivity failure and a server rejection keeps its status and message,
    // instead of everything being flattened into an "unknown network error".
    try {
      await TreeRepository.createTree(
        formData,
        () => {
          this.incrementCounter(parseInt(String(fullData.number)));
        }
      );

      // The map's stored files no longer hold this tree: the next Tree Map visit has to ask API for all trees again
      invalidateTreesListVersion();
    } catch (err) {
      console.error('tree rejected by the server', {
        idbId,
        payload: describeFormData(formData),
      });
      throw err;
    }

    await this.deleteItem(idbId, integerOrNull(fullData.number) || 0);
  }

  incrementCounter(increment: number) {
    try {
      const { counterRef } = this.context;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      counterRef?.current?.updateTrees(increment);
    } catch (error) {
      console.error('SyncQueue::updateTrees', { error, increment });
    }
  }

  async deleteItem(id: number, treesInHole?: number) {
    const { treesData, treesKeys, totalTreesInQueue } = this.state;
    await DBService.deleteValue(TABLE_TREES, id);
    // Subtracted rather than recounted: counting the trees of the queue reads every row, photo included,
    // and this runs once per tree that leaves it - every synced tree, every delete
    const removed = treesData.filter(item => item.idbId === id)[0];
    const removedTrees = treesInHole ?? (integerOrNull(removed && removed.data.number) || 0);
    const updatedTreesData = treesData.filter(item => item.idbId !== id);
    this.setState({
      treesData: updatedTreesData,
      treesKeys: treesKeys.filter((a) => a !== id),
      offlineQueueLength: Math.max(0, treesKeys.length - 1),
      totalTreesInQueue: Math.max(0, totalTreesInQueue - removedTrees),
    });
    // The tree - and the photo it carries - just left the storage this site is allowed, so the figure
    // read when it was queued is no longer the one to warn about: read again so a warning that no longer
    // applies goes away, which is what a delete from the queue is meant to achieve.
    this.refreshStorageEstimate();
  }

  updateTreeData(data: SyncQueueItemData[]) {
    const { treesData } = this.state;
    return treesData.map((item: SyncQueueItemData) => {
      const tree = data.filter((tree) => tree.idbId === item.idbId);
      return !tree ? item : { ...item, ...tree[0] };
    });
  }

  checkOnlineStatus() {
    const { onlineStatus } = this.context;
    return onlineStatus === ConnectionState.offline;
  }

  async fetchQueueData(incrementPage?: boolean) {
    const { currentPage, isFullSync, treesData } = this.state;
    const page = currentPage + (incrementPage ? 1 : 0);

    if (isFullSync) {
      this.setState({ loading: false });
      return;
    }

    // The keys only: the trees of the queue are counted once, when it mounts (`countRecordsToSync`), because
    // counting them means reading every row - a photo with a tree in it, ~10 ms each
    const treesKeys: number[] = JSON.parse(JSON.stringify(await TreeRepository.getIndexes())).reverse();
    const treeIdsRendered = treesData.map(current => current.idbId);
    const referenceTreeId = treesData[0]?.idbId;
    let treesAdded = 0;

    // The spinner over the queue goes away whatever happens here
    try {
      for (let index = 0; index < (page * this.nbTreesPerPage); index++) {
        const currentTreeId = treesKeys[index];
        if (treesData.length === treesKeys.length) {
          break;
        }
        if (treeIdsRendered.indexOf(currentTreeId) > -1) {
          continue;
        }

        const idbData = await TreeRepository.getOne(treesKeys[index]);
        const fullTreeData = this.formatTreeData(idbData, currentTreeId);
        treesAdded += integerOrNull(fullTreeData.data.number) || 0;

        if (referenceTreeId && currentTreeId > referenceTreeId) {
          treesData.unshift(fullTreeData);
        } else {
          treesData.push(fullTreeData);
        }

        // A tree is read in one go (~20 ms) and cannot be cut up, but the spinner has to keep turning
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
    } finally {
      this.setState({
        treesData,
        treesKeys,
        offlineQueueLength: treesKeys.length,
        currentPage: page,
        loading: false,
        // The rows read here were not in the queue before: the button's total grows with them, unchanged otherwise
        totalTreesInQueue: this.state.totalTreesInQueue + treesAdded,
      });
    }
  }

  formatTreeData(data: TreeFormData, id: number): SyncQueueItemData {
    return { data, idbId: id, status: 'pending' }
  }

  render() {
    const {
      syncing,
      openModal,
      queueListKey,
      treesData,
      loading,
      isFullSync,
      treesKeys,
      hasFetchError,
      syncErrorMessage,
      syncErrorIsConnectivity,
      isCountingTrees,
      syncSuccess,
      syncedCount,
      totalTreesInQueue,
      currentTreesSynced,
      initialTotalTreesInQueue,
    } = this.state

    const { treetypesList, saving } = this.props;
    const loader = <Spin size="small" />;
    // While a tree is compressed and queued (see TreeImageUpload) the count gives way to the spinner.
    // The number stays in the DOM - hidden - because the e2e tests read `.span-offline-queue-count`
    // whatever the queue is doing at that moment.
    const queueLength = <span className='span-offline-queue-count' style={saving ? { display: 'none' } : undefined}>
      {isCountingTrees ? loader : totalTreesInQueue}
    </span>;

    return (
      <>
        <Button
          type='link'
          className={'btn-open-offline-queue'}
          onClick={() => {
            // The window opens first and the queue is read from the callback below: with hundreds of trees stored,
            // that read is what takes the time, and nothing of it must hold the window back
            this.setState(
              {
                openModal: true,
                // A new key on every opening: the Modal does not re-render its children while it is hidden
                // (rc-dialog memoizes them on its own `visible`, see MemoChildren), so the queue list would
                // otherwise keep its state - the box around the last opened photo - across a close and a
                // reopen. Changing the key remounts the list, whatever the Modal does with the children.
                queueListKey: this.state.queueListKey + 1,
                syncSuccess: false,
                syncedCount: 0,
                currentTreesSynced: 0,
                initialTotalTreesInQueue: 0,
                // The spinner sits over the content until the list in it has been filled
                loading: !this.state.isFullSync,
              },
              () => {
                // Deferred to the next task, so the window and its spinner are on screen before the queue is
                // read (a page costs ~20 ms per tree, and never takes the thread away for longer than that)
                setTimeout(() => { void this.fetchQueueData(); }, 0);
              }
            );
          }}
        >
          {!isFullSync && <>show queue ({saving && loader}{queueLength})</>}
        </Button>
        <Modal
          open={openModal}
          confirmLoading
          closable={!isFullSync}
          cancelText={'Close'}
          maskClosable={false}
          okText={'Sync'}
          onOk={async () => {
            await this.sync();
          }}
          cancelButtonProps={{ disabled: syncing, style: { display: 'none' } }}
          okButtonProps={{ loading: syncing, disabled: treesKeys.length === 0 }}
          onCancel={() => this.setState({ openModal: false })}
          title={"Trees in queue"}
          footer={syncSuccess ? null : undefined}
        >
          {syncSuccess ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <img
                src="/dist/icons/success_emoticon.svg"
                // alt="Success"
                style={{ width: '100px', marginBottom: '20px' }} 
              />
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                {syncedCount} tree{ syncedCount > 1 ? 's' : '' }<br />synced successfully
              </div>
              <div style={{ marginTop: '20px' }}>
                <Button type="primary" onClick={() => this.setState({ openModal: false })}>
                  Close
                </Button>
              </div>
            </div>
          ) : (
            // The spinner lies over the queue while it is read and rendered: the window itself opens at once,
            // whatever the size of the queue
            <Spin spinning={loading} tip={'Loading the queue...'}>
              <div>
                {this.renderStorageWarning()}
                {isFullSync
                  ? <SyncQueueProgressBar
                    current={currentTreesSynced}
                    max={initialTotalTreesInQueue || totalTreesInQueue}
                  />
                  : <SyncQueueList
                    key={queueListKey}
                    hasFetchError={hasFetchError}
                    errorMessage={syncErrorMessage}
                    errorIsConnectivity={syncErrorIsConnectivity}
                    syncing={isFullSync}
                    treesData={treesData}
                    treetypesList={treetypesList}
                    deleteCallback={this.deleteItem}
                    displayLoadMore={treesKeys.length !== treesData.length}
                    loadMoreCallback={() => this.fetchQueueData(true)}
                    syncCallback={this.sync}
                  />
                }
              </div>
            </Spin>
          )}
        </Modal>
      </>
    );
  }
}

SyncQueue.contextType = GlobalContext;